/**
 * Orders API endpoints
 */
import { api } from './baseApi';
import type { OrdersResponse, OrderDetailResponse, OrderStatus, OrderStatisticsResponse } from '../../types/order';

interface OrdersParams {
  status?: OrderStatus;
  service_id?: number;
  subservice_id?: number;
  provider_type?: string;
  provider_id?: number;
  user_id?: number;
  search?: string;
  unassigned?: boolean;
  special_request?: boolean;
  /** Orders linked to a patient file */
  patient_id?: number;
  /** Request date range, YYYY-MM-DD (whole days) */
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

interface UpdateStatusRequest {
  order_id: number;
  status: OrderStatus;
  admin_notes?: string;
  /** Required when setting status to CONFIRMED (from non-confirmed). EGP, e.g. "10.00" or "0.00". */
  platform_fee?: string;
  /** Required when confirming a Home Doctor Visit order whose price is still 0 (requires_price_input). EGP, > 0. */
  service_price?: string;
}

interface AssignProviderRequest {
  provider_type: 'SPECIALIST' | 'CONSULTANT';
  provider_id: number;
  admin_notes?: string;
  /** Required when order is still PENDING (assignment confirms the order). */
  platform_fee?: string;
  /** Required when confirming a Home Doctor Visit order whose price is still 0 (requires_price_input). EGP, > 0. */
  service_price?: string;
}

interface ReassignProviderRequest {
  provider_type: 'SPECIALIST' | 'CONSULTANT';
  provider_id: number;
  reason?: string;
  /** Required if order is still PENDING (same as assign). */
  platform_fee?: string;
  /** Required when confirming a Home Doctor Visit order whose price is still 0 (requires_price_input). EGP, > 0. */
  service_price?: string;
}

/** PATCH /dashboard/orders/:id/patient-location/ — partial body, at least one field required server-side */
export interface UpdateOrderPatientLocationRequest {
  patient_name?: string;
  patient_age?: number;
  patient_gender?: 'M' | 'F';
  patient_phone?: string;
  health_conditions?: string;
  allergies?: string;
  current_medications?: string;
  special_notes?: string;
  full_address?: string;
  city?: string;
  area?: string;
  latitude?: string | null;
  longitude?: string | null;
}

/** POST /dashboard/orders/create/ — order on behalf of a customer (phone booking) */
export interface CreateOrderRequest {
  customer_phone: string;
  customer_first_name?: string;
  customer_last_name?: string;
  service_id: number;
  subservice_id: number;
  internal_subservice_id?: number | null;
  provider_type?: 'SPECIALIST' | 'CONSULTANT' | null;
  provider_id?: number | null;
  patient_name: string;
  patient_age: number;
  patient_gender: 'M' | 'F';
  patient_phone?: string | null;
  order_date: string;
  time_from: string;
  time_to: string;
  city: string;
  area?: string;
  street_address: string;
  building?: string;
  floor?: string;
  apartment?: string;
  health_conditions?: string;
  allergies?: string;
  current_medications?: string;
  special_notes?: string;
  admin_notes?: string;
  service_price?: string | null;
}

export interface CreateOrderSubServiceOption {
  id: number;
  name: string;
  name_ar: string;
  price: number;
  internal_subservices?: CreateOrderSubServiceOption[];
}

export interface CreateOrderServiceOption {
  id: number;
  name: string;
  name_ar: string;
  subservices: CreateOrderSubServiceOption[];
}

export interface CreateOrderProviderOption {
  id: number;
  full_name: string;
  provider_type: 'SPECIALIST' | 'CONSULTANT';
  price: number;
}

export interface CreateOrderCustomer {
  id: number;
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  is_active: boolean;
  orders_count: number;
}

export interface CreateOrderPreviousPatient {
  full_name: string;
  age: number;
  gender: 'M' | 'F';
  phone_number: string;
  health_conditions: string;
  allergies: string;
  current_medications: string;
}

export interface CreateOrderPreviousAddress {
  street_address: string;
  building: string;
  floor: string;
  apartment: string;
  area: string;
  city: string;
  latitude: string | null;
  longitude: string | null;
}

export interface CreateOrderCustomerHistory {
  customer: CreateOrderCustomer;
  patients: CreateOrderPreviousPatient[];
  addresses: CreateOrderPreviousAddress[];
}

export const ordersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getOrders: builder.query<OrdersResponse, OrdersParams>({
      query: (params) => ({
        url: '/dashboard/orders/',
        params,
      }),
      providesTags: ['Orders'],
      keepUnusedDataFor: 0,
    }),
    getOrder: builder.query<OrderDetailResponse, number>({
      query: (id) => `/dashboard/orders/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'Order', id }],
    }),
    updateOrderStatus: builder.mutation<OrderDetailResponse, UpdateStatusRequest>({
      query: ({ order_id, ...body }) => ({
        url: `/dashboard/orders/${order_id}/status/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { order_id }) => [
        { type: 'Order', id: order_id },
        'Orders',
        'Statistics',
      ],
    }),
    assignProvider: builder.mutation<OrderDetailResponse, { order_id: number } & AssignProviderRequest>({
      query: ({ order_id, ...body }) => ({
        url: `/dashboard/orders/${order_id}/assign-provider/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { order_id }) => [
        { type: 'Order', id: order_id },
        'Orders',
        'Statistics',
      ],
    }),
    reassignProvider: builder.mutation<OrderDetailResponse, { order_id: number } & ReassignProviderRequest>({
      query: ({ order_id, ...body }) => ({
        url: `/dashboard/orders/${order_id}/reassign-provider/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { order_id }) => [
        { type: 'Order', id: order_id },
        'Orders',
        'Statistics',
      ],
    }),
    updateOrderPatientLocation: builder.mutation<
      OrderDetailResponse,
      { order_id: number } & UpdateOrderPatientLocationRequest
    >({
      query: ({ order_id, ...body }) => ({
        url: `/dashboard/orders/${order_id}/patient-location/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { order_id }) => [
        { type: 'Order', id: order_id },
        'Orders',
        'Statistics',
      ],
    }),
    createOrder: builder.mutation<OrderDetailResponse, CreateOrderRequest>({
      query: (body) => ({
        url: '/dashboard/orders/create/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Orders', 'Statistics'],
    }),
    getCreateOrderServices: builder.query<{ data: { services: CreateOrderServiceOption[] } }, void>({
      query: () => '/dashboard/orders/create/options/',
    }),
    getCreateOrderProviders: builder.query<{ data: { providers: CreateOrderProviderOption[] } }, number>({
      query: (subservice_id) => ({
        url: '/dashboard/orders/create/options/',
        params: { subservice_id },
      }),
    }),
    lookupOrderCustomer: builder.query<{ data: { customer: CreateOrderCustomer | null } }, string>({
      query: (phone) => ({
        url: '/dashboard/orders/create/options/',
        params: { phone },
      }),
      keepUnusedDataFor: 0,
    }),
    searchOrderCustomers: builder.query<{ data: { customers: CreateOrderCustomer[] } }, string>({
      query: (search) => ({
        url: '/dashboard/orders/create/options/',
        params: { search },
      }),
      keepUnusedDataFor: 30,
    }),
    getOrderCustomerHistory: builder.query<{ data: CreateOrderCustomerHistory }, number>({
      query: (customer_id) => ({
        url: '/dashboard/orders/create/options/',
        params: { customer_id },
      }),
      keepUnusedDataFor: 0,
    }),
    getOrderStatistics: builder.query<OrderStatisticsResponse, void>({
      query: () => '/dashboard/orders/statistics/',
      providesTags: ['Statistics'],
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useGetOrderQuery,
  useUpdateOrderStatusMutation,
  useAssignProviderMutation,
  useReassignProviderMutation,
  useUpdateOrderPatientLocationMutation,
  useGetOrderStatisticsQuery,
  useCreateOrderMutation,
  useGetCreateOrderServicesQuery,
  useGetCreateOrderProvidersQuery,
  useLazyLookupOrderCustomerQuery,
  useSearchOrderCustomersQuery,
  useLazyGetOrderCustomerHistoryQuery,
} = ordersApi;

